/**
 * 应用根 —— 路由守卫 + 布局装配 + 自动锁定。
 *
 * 守卫规则（ARCH §5.4）：
 *  - 本机无身份        → `/onboarding`（首次）或 `/welcome`
 *  - 有身份但未解锁    → `/unlock`（锁定态独占路由）
 *  - 已解锁            → 主界面
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppRoutes, ROUTE_PATH } from '@/router';
import { AppShell } from '@/components/Layout/AppShell';
import { ToastHost } from '@/components/ui/Toast';
import { LoadingBlock } from '@/components/ui/Spinner';
import { useAutoLock } from '@/hooks/useAutoLock';
import { useIdentityStore } from '@/store/identity';
import { useSettingsStore } from '@/store/settings';
import { relayLamp, useNetworkStore } from '@/store/network';
import { container } from '@/services/container';
import { startSession, stopSession } from '@/services/session';
import { LS_KEYS } from '@/config';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';

const logger = createLogger('app');

/** 认证流程路由（这些页面不需要 AppShell 包裹）。 */
const AUTH_ROUTES: string[] = [
  ROUTE_PATH.onboarding,
  ROUTE_PATH.welcome,
  ROUTE_PATH.create,
  ROUTE_PATH.backup,
  ROUTE_PATH.import,
  ROUTE_PATH.unlock,
];

export function App(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const status = useIdentityStore((s) => s.status);
  const setNone = useIdentityStore((s) => s.setNone);
  const setError = useIdentityStore((s) => s.setError);
  const hydrateSettings = useSettingsStore((s) => s.hydrate);

  /** 全局状态灯：跟随真实中继链路态（而非写死的「离线」）。 */
  const relayStatus = useNetworkStore((s) => s.relay.status);
  const lamp = useMemo(() => relayLamp(relayStatus), [relayStatus]);

  const [booted, setBooted] = useState(false);

  /** 启动：探测本机身份，决定落到哪个路由。
   *  关键：引导路由决策只在「应用停留在入口 '/'」时由本 effect 接管一次。
   *  若 hasIdentity() 异步结果迟到（或 StrictMode 产生多个挂起 Promise），
   *  此时用户早已导航到 /welcome、/create 等页面，pathname !== '/'，
   *  则不再执行 navigate，避免覆盖用户已发生的跳转（修复「点击创建身份没反应」）。 */
  const bootRan = useRef(false);
  useEffect(() => {
    if (bootRan.current) return; // StrictMode 双调用只跑一次
    bootRan.current = true;
    void (async () => {
      try {
        const has = await container.identity.hasIdentity();

        // 无论是否导航，都先确定身份状态
        if (!has) {
          setNone();
        } else {
          // 有身份 → 标记锁定态（绝不自动解锁）
          const meta = await container.identity.loadMeta();
          if (meta) useIdentityStore.getState().setLocked(meta);
        }

        // 仅在仍停留在入口时才接管路由，绝不事后覆盖用户跳转
        if (window.location.pathname === '/') {
          if (!has) {
            let onboarded = false;
            try {
              onboarded = localStorage.getItem(LS_KEYS.onboarded) === '1';
            } catch {
              onboarded = false;
            }
            navigate(onboarded ? ROUTE_PATH.welcome : ROUTE_PATH.onboarding, { replace: true });
          } else {
            navigate(ROUTE_PATH.unlock, { replace: true });
          }
        }

        await hydrateSettings();
      } catch (err) {
        logger.error('启动探测失败', { err });
        setError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
      } finally {
        setBooted(true);
      }
    })();
  }, [navigate, setError, setNone, hydrateSettings]);

  /** 守卫：已锁定却在主界面 → 强制回 /unlock；已解锁却在认证页 → 回主界面。 */
  useEffect(() => {
    if (!booted) return;
    const isAuthRoute = AUTH_ROUTES.includes(location.pathname);

    if (status === 'locked' && !isAuthRoute) {
      navigate(ROUTE_PATH.unlock, { replace: true });
    } else if (status === 'unlocked' && (location.pathname === ROUTE_PATH.unlock || location.pathname === ROUTE_PATH.welcome)) {
      navigate(ROUTE_PATH.chat, { replace: true });
    }
  }, [booted, location.pathname, navigate, status]);

  /** 空闲 15 分钟自动锁定。 */
  useAutoLock({
    enabled: status === 'unlocked',
    onLock: () => navigate(ROUTE_PATH.unlock, { replace: true }),
  });

  /** 解锁后装配传输 / 内容 / Feed / Chat 服务；锁定后停止会话。 */
  useEffect(() => {
    if (status === 'unlocked') {
      void startSession().catch((err) => logger.error('会话启动失败', { err }));
    } else if (status === 'locked') {
      void stopSession().catch(() => undefined);
    }
  }, [status]);

  const isAuthRoute = useMemo(() => AUTH_ROUTES.includes(location.pathname), [location.pathname]);

  if (!booted) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingBlock text="正在加载本地身份…" />
      </div>
    );
  }

  if (isAuthRoute) {
    return (
      <>
        <AppRoutes />
        <ToastHost />
      </>
    );
  }

  return (
    <>
      <AppShell
        networkLabel={lamp.label}
        networkTone={lamp.tone}
        onLock={() => {
          void stopSession();
          useIdentityStore.getState().lock();
          navigate(ROUTE_PATH.unlock, { replace: true });
        }}
      >
        <AppRoutes />
      </AppShell>
      <ToastHost />
    </>
  );
}

export default App;
